// @ts-check
// Configurações > Modelos do WhatsApp (contrato 08 de leads e WhatsApp, seção 7): todos os modelos de todos os números
// da escola, com situação, categoria, idioma, número e prévia; busca, filtro por situação e por número; "Novo modelo",
// "Sincronizar com a Meta" e excluir para quem tem a flag WORKSPACE (as mesmas permissões das rotas). Com ?new=1 a tela
// abre direto no formulário de criação (atalho "Criar modelo" da conversa e da Nova conversa) e, com ?from=, oferece
// "Voltar para onde você estava". Desde a 2.0.0 (contrato 09 de campanhas de WhatsApp, seção 2), também o filtro por
// categoria da Meta (?category=) e os selos de qualidade, categoria, botão de saída, comunicado e uso nos cartões.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { PageCard, PageHeader } from '../../page/page-card.js';
import { SettingsSection } from '../settings-pages.js';
import { navigate, searchParamsState, updateSearchParams } from '../../../app/router.js';
import { hasCurrentPermissionFlag } from '../../../data/metadata-store.js';
import { listChannels } from '../../../data/conversations.js';
import { deleteWhatsappTemplate, listWhatsappTemplates, syncWhatsappTemplates } from '../../../data/integrations.js';
import { getErrorMessage } from '../../../data/api-client.js';
import { Button } from '../../../ui/button.js';
import { Icon } from '../../../ui/icon.js';
import { Modal } from '../../../ui/modal.js';
import { Skeleton } from '../../../ui/feedback.js';
import { enqueueSnackbar, showErrorSnackbar, showSuccessSnackbar } from '../../../ui/snackbar.js';
import {
  TEMPLATE_CATEGORY_FILTERS, TEMPLATE_STATUS_GROUP_KEYS, channelLabel, filterTemplates, readReturnPath, summarizeTemplates, templatesPageState,
} from '../../../lib/whatsapp-templates.js';
import { WhatsappTemplateCard } from './whatsapp-template-card.js';
import { CreateTemplateModal } from './create-template-modal.js';
import { t } from '../../../i18n/index.js';

/**
 * @typedef {import('../../../data/integrations.js').WhatsappTemplate} WhatsappTemplate
 * @typedef {import('../../../data/conversations.js').Channel} Channel
 */

// modelos em análise: a lista se atualiza sozinha por um tempo, com a aba visível
const REVIEW_REFRESH_MILLISECONDS = 20_000;
const REVIEW_REFRESH_LIMIT_MILLISECONDS = 10 * 60_000;
const STATUS_CHIPS = /** @type {const} */ (['ALL', ...TEMPLATE_STATUS_GROUP_KEYS]);

export function SettingsWhatsappTemplatesPage() {
  const canManage = hasCurrentPermissionFlag('WORKSPACE');
  const [channels, setChannels] = useState(/** @type {Channel[] | null} */ (null));
  const [templates, setTemplates] = useState(/** @type {WhatsappTemplate[] | null} */ (null));
  const [loadFailed, setLoadFailed] = useState(false);
  const [search, setSearch] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [creation, setCreation] = useState(/** @type {{ channelId: string | null } | null} */ (null));
  const [pendingDelete, setPendingDelete] = useState(/** @type {WhatsappTemplate | null} */ (null));
  const [isDeleting, setIsDeleting] = useState(false);
  const [highlightedId, setHighlightedId] = useState(/** @type {string | null} */ (null));
  const reviewStartedAtRef = useRef(Date.now());
  const parameters = searchParamsState.value;
  // tela de origem do atalho "Criar modelo", lida uma vez (o endereço perde o parâmetro depois de abrir o formulário)
  const [returnPath] = useState(() => readReturnPath(parameters.get('from')));
  const goBack = () => {
    if (returnPath) navigate(returnPath);
  };
  const statusGroup = parameters.get('status') ?? 'ALL';
  const channelFilter = parameters.get('number');
  const categoryParameter = parameters.get('category');
  const categoryFilter = categoryParameter && TEMPLATE_CATEGORY_FILTERS.includes(categoryParameter) ? categoryParameter : 'ALL';

  const loadTemplates = () => listWhatsappTemplates({ withUses: true }).then((list) => {
    setTemplates(list);
    setLoadFailed(false);
    return list;
  });
  const load = () => {
    setLoadFailed(false);
    Promise.all([listChannels(), listWhatsappTemplates({ withUses: true })])
      .then(([channelData, list]) => {
        setChannels(channelData.channels.filter((channel) => channel.type === 'WHATSAPP'));
        setTemplates(list);
      })
      .catch((error) => {
        // falha não vira "nenhum número" nem "nenhum modelo": o que já estava na tela fica, e a primeira carga mostra o erro
        showErrorSnackbar(error);
        setLoadFailed(true);
      });
  };

  useEffect(() => {
    document.title = `${t('whatsappTemplates.title')} - ${t('navigation.settings')}`;
    load();
  }, []);

  const whatsappChannels = channels ?? [];
  const channelsWithWaba = whatsappChannels.filter((channel) => Boolean(channel.settings?.wabaId));
  const channelsById = new Map(whatsappChannels.map((channel) => [channel.id, channel]));

  // ?new=1 (atalho "Criar modelo"): abre o formulário depois de carregar, só para quem pode criar
  useEffect(() => {
    if (channels === null || parameters.get('new') !== '1') return;
    const requestedChannelId = parameters.get('channelId');
    if (canManage && channelsWithWaba.length > 0) {
      setCreation({ channelId: channelsWithWaba.some((channel) => channel.id === requestedChannelId) ? requestedChannelId : null });
    }
    updateSearchParams({ new: null, channelId: null, from: null });
  }, [channels]);

  // modelos em análise: atualiza a cada 20 s com a aba visível, por até 10 minutos
  const hasInReview = (templates ?? []).some((template) => template.status === 'PENDING' || template.status === 'IN_APPEAL');
  useEffect(() => {
    if (!hasInReview) return undefined;
    const interval = setInterval(() => {
      if (Date.now() - reviewStartedAtRef.current > REVIEW_REFRESH_LIMIT_MILLISECONDS) {
        clearInterval(interval);
        return;
      }
      if (document.visibilityState === 'visible') loadTemplates().catch(() => null);
    }, REVIEW_REFRESH_MILLISECONDS);
    return () => clearInterval(interval);
  }, [hasInReview]);

  const sync = async () => {
    const targets = channelsWithWaba;
    if (targets.length === 0) return;
    setIsSyncing(true);
    /** @type {Array<{ channel: Channel, error: string }>} */
    const failures = [];
    for (const channel of targets) {
      try {
        await syncWhatsappTemplates(channel.id);
      } catch (error) {
        failures.push({ channel, error: getErrorMessage(error) });
      }
    }
    setIsSyncing(false);
    if (failures.length === 0) showSuccessSnackbar(t('whatsappTemplates.sync.done'));
    else {
      enqueueSnackbar({
        variant: 'error',
        message: t('whatsappTemplates.sync.partial', { ok: targets.length - failures.length, total: targets.length, number: channelLabel(failures[0].channel), error: failures[0].error }),
      });
    }
    reviewStartedAtRef.current = Date.now();
    await loadTemplates().catch(showErrorSnackbar);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      await deleteWhatsappTemplate(pendingDelete.id);
      showSuccessSnackbar(t('whatsappTemplates.deleteConfirm.deleted'));
      setPendingDelete(null);
      await loadTemplates();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const onCreated = (/** @type {WhatsappTemplate} */ template) => {
    setCreation(null);
    setHighlightedId(template?.id ?? null);
    reviewStartedAtRef.current = Date.now();
    loadTemplates().then(() => {
      if (!template?.id) return;
      requestAnimationFrame(() => document.getElementById(`template-${template.id}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
    }).catch(showErrorSnackbar);
  };

  const all = templates ?? [];
  const summary = summarizeTemplates(channelFilter ? all.filter((template) => template.channelId === channelFilter) : all);
  const filtered = filterTemplates(all, { search, statusGroup, channelId: channelFilter, category: categoryFilter });
  const hasFilters = Boolean(search.trim()) || statusGroup !== 'ALL' || Boolean(channelFilter) || categoryFilter !== 'ALL';
  const pageState = templatesPageState({ channels: channels === null ? null : whatsappChannels, templates, loadFailed });
  const isLoading = pageState === 'LOADING';

  const actions = canManage && !isLoading && whatsappChannels.length > 0
    ? html`<div class="templates-header-actions">
        <${Button} title=${isSyncing ? t('whatsappTemplates.actions.syncing') : t('whatsappTemplates.actions.sync')} icon="IconRefresh" variant="secondary" size="small"
          isLoading=${isSyncing} disabled=${channelsWithWaba.length === 0} onClick=${sync} />
        <${Button} title=${t('whatsappTemplates.actions.new')} icon="IconPlus" accent="blue" size="small" disabled=${channelsWithWaba.length === 0}
          onClick=${() => setCreation({ channelId: channelFilter })} />
      </div>`
    : null;

  /** @param {string} key */
  const statusChip = (key) => html`<button key=${key} type="button" class="templates-chip" aria-pressed=${statusGroup === key ? 'true' : 'false'}
    onClick=${() => updateSearchParams({ status: key === 'ALL' ? null : key })}>
    <span>${t(`whatsappTemplates.statusGroups.${key}`)}</span><span class="templates-chip-count">${summary.counts[key] ?? 0}</span>
  </button>`;

  /** @param {string} key */
  const categoryChip = (key) => html`<button key=${key} type="button" class="templates-chip" aria-pressed=${categoryFilter === key ? 'true' : 'false'}
    onClick=${() => updateSearchParams({ category: key === 'ALL' ? null : key })}>
    <span>${t(`whatsappTemplates.categoryFilters.${key}`)}</span><span class="templates-chip-count">${summary.byCategory[key] ?? 0}</span>
  </button>`;

  const loadFailedBlock = html`<div class="integration-empty templates-empty" role="alert"><p>${t('whatsappTemplates.empty.loadFailed')}</p>
    <${Button} title=${t('whatsappTemplates.actions.retry')} icon="IconRefresh" variant="secondary" size="small" onClick=${load} /></div>`;

  const renderBody = () => {
    if (pageState === 'LOADING') return html`<div class="templates-skeleton"><${Skeleton} height=${44} radius=${8} /><${Skeleton} height=${160} radius=${8} /><${Skeleton} height=${160} radius=${8} /></div>`;
    if (pageState === 'LOAD_FAILED') return loadFailedBlock;
    if (pageState === 'NO_CHANNEL') {
      return html`<div class="integration-empty templates-empty">
        <p>${t('whatsappTemplates.empty.noChannel')}</p>
        <${Button} title=${t('whatsappTemplates.actions.goToChannels')} icon="IconPlugConnected" variant="secondary" size="small" onClick=${() => navigate('/settings/channels')} />
      </div>`;
    }
    return html`
      ${whatsappChannels.filter((channel) => !channel.settings?.wabaId).map((channel) => html`<p key=${channel.id} class="channel-notice" data-tone="warning" role="status">
        <${Icon} name="IconAlertTriangle" size=${16} /><span class="channel-notice-text">${t('whatsappTemplates.noWaba', { number: channelLabel(channel) })}</span>
      </p>`)}
      <div class="templates-toolbar">
        <label class="data-model-search templates-search">
          <${Icon} name="IconSearch" size=${16} />
          <input class="text-input" type="search" value=${search} placeholder=${t('whatsappTemplates.search.placeholder')} aria-label=${t('whatsappTemplates.search.placeholder')}
            onInput=${(event) => setSearch(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
        </label>
        ${whatsappChannels.length > 1
          ? html`<label class="templates-number-filter">
              <span class="visually-hidden-text">${t('whatsappTemplates.filters.channel')}</span>
              <select class="text-input integration-select" value=${channelFilter ?? ''} aria-label=${t('whatsappTemplates.filters.channel')}
                onChange=${(/** @type {Event} */ event) => updateSearchParams({ number: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null })}>
                <option value="">${t('whatsappTemplates.filters.allChannels')}</option>
                ${whatsappChannels.map((channel) => html`<option key=${channel.id} value=${channel.id} selected=${channel.id === channelFilter}>${channelLabel(channel)}</option>`)}
              </select>
            </label>`
          : null}
      </div>
      <div class="templates-chips" role="group" aria-label=${t('whatsappTemplates.filters.status')}>${STATUS_CHIPS.map(statusChip)}</div>
      <div class="templates-chips" role="group" aria-label=${t('whatsappTemplates.categoryFilters.label')}>${TEMPLATE_CATEGORY_FILTERS.map(categoryChip)}</div>
      ${loadFailed
        ? loadFailedBlock
        : all.length === 0
          ? html`<div class="integration-empty templates-empty">
              <p>${t('whatsappTemplates.empty.noTemplates')}</p>
              ${canManage
                ? html`<div class="templates-empty-actions">
                    <${Button} title=${t('whatsappTemplates.actions.sync')} icon="IconRefresh" variant="secondary" size="small" isLoading=${isSyncing} disabled=${channelsWithWaba.length === 0} onClick=${sync} />
                    <${Button} title=${t('whatsappTemplates.actions.new')} icon="IconPlus" accent="blue" size="small" disabled=${channelsWithWaba.length === 0} onClick=${() => setCreation({ channelId: null })} />
                  </div>`
                : html`<p>${t('whatsappTemplates.empty.noTemplatesMember')}</p>`}
            </div>`
          : filtered.length === 0
            ? html`<div class="integration-empty templates-empty"><p>${t('whatsappTemplates.empty.noResults')}</p>
                ${hasFilters ? html`<${Button} title=${t('whatsappTemplates.actions.clearFilters')} icon="IconFilterOff" variant="secondary" size="small"
                  onClick=${() => {
                    setSearch('');
                    updateSearchParams({ status: null, number: null, category: null });
                  }} />` : null}</div>`
            : html`<ul class="templates-list">
                ${filtered.map((template) => html`<${WhatsappTemplateCard} key=${template.id} template=${template} numberLabel=${whatsappChannels.length > 1 ? channelLabel(channelsById.get(template.channelId)) : ''}
                  canManage=${canManage} isHighlighted=${template.id === highlightedId} onDelete=${setPendingDelete} />`)}
              </ul>`}
    `;
  };

  return html`<${PageCard} className="templates-page-card" header=${html`<${PageHeader} icon="IconBrandWhatsapp" title=${t('whatsappTemplates.title')} actions=${actions} />`}>
    <div class="settings-page templates-page">
      <${SettingsSection} title=${t('whatsappTemplates.title')} description=${t('whatsappTemplates.description')}>
        ${actions ? html`<div class="templates-mobile-actions">${actions}</div>` : null}
        ${returnPath
          ? html`<p class="templates-return"><a class="integration-link" href=${returnPath} onClick=${(/** @type {MouseEvent} */ event) => {
              if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
              event.preventDefault();
              goBack();
            }}><${Icon} name="IconArrowLeft" size=${14} />${t('whatsappTemplates.actions.goBack')}</a></p>`
          : null}
        ${!canManage ? html`<p class="channel-card-note"><${Icon} name="IconLock" size=${14} /><span>${t('whatsappTemplates.adminNote')}</span></p>` : null}
        ${renderBody()}
      <//>
    </div>
    ${creation
      ? html`<${CreateTemplateModal} channels=${channelsWithWaba} initialChannelId=${creation.channelId} onClose=${() => setCreation(null)} onCreated=${onCreated}
          successAction=${returnPath ? { label: t('whatsappTemplates.actions.goBack'), onClick: goBack } : null} />`
      : null}
    ${pendingDelete
      ? html`<${Modal} title=${t('whatsappTemplates.deleteConfirm.title', { name: pendingDelete.name })} subtitle=${t('whatsappTemplates.deleteConfirm.text')}
          confirmLabel=${t('whatsappTemplates.deleteConfirm.confirm')} confirmAccent="danger" isLoading=${isDeleting} onClose=${() => setPendingDelete(null)} onConfirm=${confirmDelete} />`
      : null}
  <//>`;
}
